import ApiClient from './api';

/**
 * Business configuration service for managing business profiles and calendar settings
 * New service for business owner configuration capabilities
 */
class BusinessConfigService {
  constructor() {
    this.api = new ApiClient();
  }

  /**
   * Get business profile configuration
   * @param {string} customerId - Customer ID
   * @returns {Promise<Object>} Business profile data
   */
  async getBusinessConfig(customerId) {
    try {
      const response = await this.api.get(`/api/v1/business/config/${customerId}`);
      return response.business;
    } catch (error) {
      console.error('Error fetching business config:', error);
      throw error;
    }
  }

  /**
   * Update business profile configuration
   * @param {string} customerId - Customer ID
   * @param {Object} businessData - Business profile data to update
   * @returns {Promise<Object>} Updated business profile
   */
  async updateBusinessConfig(customerId, businessData) {
    try {
      const response = await this.api.put(`/api/v1/business/config/${customerId}`, businessData);
      return response.business;
    } catch (error) {
      console.error('Error updating business config:', error);
      throw error;
    }
  }

  /**
   * Get all calendars for business
   * @param {string} customerId - Customer ID
   * @returns {Promise<Array>} Array of calendar configurations
   */
  async getBusinessCalendars(customerId) {
    try {
      const response = await this.api.get(`/api/v1/business/calendars/${customerId}`);
      return response.calendars;
    } catch (error) {
      console.error('Error fetching business calendars:', error);
      throw error;
    }
  }

  /**
   * Update calendar configuration
   * @param {string} customerId - Customer ID
   * @param {string} calendarName - Calendar name
   * @param {Object} calendarData - Calendar configuration data to update
   * @returns {Promise<Object>} Updated calendar configuration
   */
  async updateCalendarConfig(customerId, calendarName, calendarData) {
    try {
      const response = await this.api.put(
        `/api/v1/business/calendars/${customerId}/${calendarName}`,
        calendarData
      );
      return response.calendar;
    } catch (error) {
      console.error('Error updating calendar config:', error);
      throw error;
    }
  }

  /**
   * Get notification templates for a calendar
   * @param {string} customerId - Customer ID
   * @param {string} calendarName - Calendar name
   * @returns {Promise<Object>} Notification templates with available placeholders
   */
  async getNotificationTemplates(customerId, calendarName) {
    try {
      const response = await this.api.get(
        `/api/v1/calendar/notifications?customer_id=${customerId}&calendar_name=${encodeURIComponent(calendarName)}`
      );
      return {
        templates: response.notification_templates || {},
        placeholders: response.available_placeholders || []
      };
    } catch (error) {
      console.error('Error fetching notification templates:', error);
      throw error;
    }
  }

  /**
   * Update notification templates for a calendar
   * @param {string} customerId - Customer ID
   * @param {string} calendarName - Calendar name
   * @param {Object} notificationTemplates - Notification templates object
   * @returns {Promise<Object>} Updated notification templates
   */
  async updateNotificationTemplates(customerId, calendarName, notificationTemplates) {
    try {
      const response = await this.api.post('/api/v1/calendar/notifications', {
        customer_id: customerId,
        calendar_name: calendarName,
        notification_templates: notificationTemplates
      });
      return response.notification_templates;
    } catch (error) {
      console.error('Error updating notification templates:', error);
      throw error;
    }
  }

  /**
   * Get confirmation configuration (global or calendar-specific)
   * @param {string} customerId - Customer ID
   * @param {string} calendarName - Optional calendar name for calendar-specific config
   * @returns {Promise<Object>} Confirmation configuration
   */
  async getConfirmationConfig(customerId, calendarName = null) {
    try {
      let url = `/api/v1/calendar/confirmation?customer_id=${customerId}`;
      if (calendarName) {
        url += `&calendar_name=${encodeURIComponent(calendarName)}`;
      }
      const response = await this.api.get(url);
      return {
        config: response.confirmation_config || {},
        scope: response.scope || 'global'
      };
    } catch (error) {
      console.error('Error fetching confirmation config:', error);
      throw error;
    }
  }

  /**
   * Get global settings for the business (business-level defaults)
   * @param {string} customerId - Customer ID
   * @returns {Promise<Object>} Global settings configuration
   */
  async getGlobalSettings(customerId) {
    try {
      const response = await this.api.get(`/api/v1/business/global-settings/${customerId}`);
      return {
        config: response.global_settings || {}
      };
    } catch (error) {
      console.error('Error fetching global settings:', error);
      throw error;
    }
  }

  /**
   * Update global settings (business-level defaults) and propagate to non-overridden calendars
   * @param {string} customerId - Customer ID
   * @param {Object} globalSettings - Global settings object (confirmation, reminders, notifications, etc.)
   * @returns {Promise<Object>} Updated global settings
   */
  async updateGlobalSettings(customerId, globalSettings) {
    try {
      const response = await this.api.post(`/api/v1/business/global-settings/${customerId}`, {
        global_settings: globalSettings
      });
      return response.global_settings;
    } catch (error) {
      console.error('Error updating global settings:', error);
      throw error;
    }
  }

  /**
   * Reset calendar to use global settings (remove override)
   * @param {string} customerId - Customer ID
   * @param {string} calendarName - Calendar name
   * @param {string} settingType - Type of setting to reset (confirmation, reminders, notifications, etc.)
   * @returns {Promise<Object>} Updated calendar configuration
   */
  async resetToGlobalSettings(customerId, calendarName, settingType) {
    try {
      const response = await this.api.post(
        `/api/v1/business/calendars/${customerId}/${calendarName}/reset-to-global`,
        { setting_type: settingType }
      );
      return response.calendar;
    } catch (error) {
      console.error('Error resetting to global settings:', error);
      throw error;
    }
  }

  /**
   * Update confirmation configuration with override flag
   * @param {string} customerId - Customer ID
   * @param {Object} confirmationConfig - Confirmation configuration object
   * @param {string} calendarName - Calendar name for calendar-specific config
   * @param {boolean} overrideGlobal - Whether to override global settings
   * @returns {Promise<Object>} Updated confirmation configuration
   */
  async updateConfirmationConfig(customerId, confirmationConfig, calendarName = null, overrideGlobal = false) {
    try {
      const payload = {
        customer_id: customerId,
        confirmation_config: confirmationConfig,
        override_global: overrideGlobal
      };
      if (calendarName) {
        payload.calendar_name = calendarName;
      }
      const response = await this.api.post('/api/v1/calendar/confirmation', payload);
      return response.confirmation_config;
    } catch (error) {
      console.error('Error updating confirmation config:', error);
      throw error;
    }
  }
}

export const businessConfigService = new BusinessConfigService();
